/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { RichTextComponent } from '@msdyn365-commerce/core';
import * as React from 'react';
import { Club } from '../../../modules/wine-club-signup/./components/club';
import { Membership } from '../../../modules/wine-club-signup/./components/membership';
import { Shipping } from '../../../modules/wine-club-signup/./components/shipping';
import { Signup } from '../../../modules/wine-club-signup/./components/signup';
import { Variant } from '../../../modules/wine-club-signup/./components/variant';
import { WinePreference } from '../../../modules/wine-club-signup/./components/wine-preference';
import { IWineClubSignupViewProps } from '../../../modules/wine-club-signup/./wine-club-signup';
import { WineClubDataStore } from '../../../modules/wine-club-signup/wine-club-data';

import { IWineClubSignupProps } from './../definition-extensions/wine-club-signup.ext.props.autogenerated';

export namespace ClubViewManager {
    export const renderClubs = (props: IWineClubSignupViewProps) => {
        return (
            <div className='wine-club-signup-club'>
                {/* this is here and NOT in the club because each club is rendered seperately in an ordered list*/}
                <h2 className='wine-club-signup-club-title'>{props.resources.wineClubSignup__stepOne}</h2>
                <div
                    className={
                        `wine-club-signup-club-items wine-club-signup-blend-membership-${BlendConfirmSMWEMember.isSmweMember}`
                    }
                >
                    {props.clubInfo.map((club, idx) => {
                        return (
                            <Club
                                key={idx}
                                club={club}
                                resources={props.resources}
                                id={idx}
                                gridSettings={props.context.request.gridSettings || {}}
                                imageSettings={props.clubImageSettings}
                            />
                        );
                    })}
                </div>
            </div>
        );
    };

    export const shipping = (props: IWineClubSignupViewProps) => {
        return (
            <Shipping
                clubInfo={props.clubInfo}
                props={props}
            />
        );
    };

    export const renderSignup = (props: IWineClubSignupViewProps) => {
        return (
            <React.Fragment>
                <Signup props={props} wineCheckout={props.config.wineCheckout} />
                {props.config.signupText && <RichTextComponent className='wine-club-signup-signup-message' text={props.config.signupText} />}
            </React.Fragment>
        );
    };

    export const winePreference = (props: IWineClubSignupViewProps) => {
        return <WinePreference clubInfo={props.clubInfo} config={props.config} resources={props.resources} />;
    };

    export const variant = (props: IWineClubSignupViewProps) => {
        return (
            <Variant
                clubInfo={props.clubInfo}
                config={props.config}
                resources={props.resources}
                gridSettings={props.context.request.gridSettings || {}}
                imageSettings={props.variantImageSettings}
            />
        );
    };

    export const membershipSelector = (props: IWineClubSignupViewProps) => {
        return <Membership config={props.config} resources={props.resources} />;
    };

    export const termsAndConditions = (props: IWineClubSignupViewProps) => {
        return (
            <div className='wine-club-signup-terms'>
                <h2 className='wine-club-signup-terms-title'>{props.config.termsConditions?.title || props.resources.wineClubSignup__stepFive}</h2>
                {props.config.termsConditions?.subtitle && <RichTextComponent className='wine-club-signup-terms-subtitle' text={props.config.termsConditions.subtitle} />}
                {props.slots.tos}
            </div>
        );
    };
}

interface IBlendConfirmSMWEMember {
    wineClubProps: IWineClubSignupViewProps & IWineClubSignupProps<{}>;
}

enum MEMBER_OPTION {
    IS_MEMBER,
    NOT_MEMBER
}

/**
 * component for asking customer about their club membership status blend only
 */
class BlendConfirmSMWEMember extends React.Component<IBlendConfirmSMWEMember> {

    public static isSmweMember: boolean | null = null;

    constructor(props: IBlendConfirmSMWEMember) {
        super(props);

        this._onConfirmSelect = this._onConfirmSelect.bind(this);
    }

    public render(): JSX.Element {
        const {
            config: { blendTitleSMWEMemberStatus, blendConfirmSMWEMemberStatus },
            resources: { wineClubSignup__blendTitle }
        } = this.props.wineClubProps;

        return (
            <div className='wine-club-signup-blend'>
                <h2 className='wine-club-signup-blend-title'>{blendTitleSMWEMemberStatus || wineClubSignup__blendTitle}</h2>
                {
                    blendConfirmSMWEMemberStatus &&
                    <RichTextComponent
                        className='wine-club-signup-blend-subtitle'
                        text={blendConfirmSMWEMemberStatus}
                    />
                }
                <div className='wine-club-signup-blend-buttons'>
                <button
                        onClick={this._onConfirmSelect}
                        data-member-type={MEMBER_OPTION.NOT_MEMBER}
                        disabled={BlendConfirmSMWEMember.isSmweMember === false}
                        className='wine-club-signup-blend-buttons-no'
                    >
                        NO
                    </button>
                    <button
                        onClick={this._onConfirmSelect}
                        data-member-type={MEMBER_OPTION.IS_MEMBER}
                        disabled={BlendConfirmSMWEMember.isSmweMember === true}
                        className='wine-club-signup-blend-buttons-yes'
                    >
                        YES
                    </button>
                </div>
            </div>
        );
    }

    private _onConfirmSelect(event: React.MouseEvent<HTMLButtonElement>): void {
        const selectType = (event.target as HTMLButtonElement).dataset.memberType;
        if (parseInt(selectType || '', 10) === MEMBER_OPTION.IS_MEMBER) {
            BlendConfirmSMWEMember.isSmweMember = true;
        }
        if (parseInt(selectType || '', 10) === MEMBER_OPTION.NOT_MEMBER) {
            BlendConfirmSMWEMember.isSmweMember = false;
        }
        WineClubDataStore.getInstance().reset();
        this.props.wineClubProps.triggerRender();
    }
}

export default (props: IWineClubSignupViewProps & IWineClubSignupProps<{}>) => {
    // enable props.debug to view localstorage data
    return (
        <div className='wine-club-signup'>
            {/* {props.debug} */}
            <BlendConfirmSMWEMember wineClubProps={props} />
            {BlendConfirmSMWEMember.isSmweMember !== null &&
                <React.Fragment>
                    {props.moduleControl.CHOOSE_CLUB && ClubViewManager.renderClubs(props)}
                    {props.moduleControl.CHOOSE_PREFERENCE && ClubViewManager.winePreference(props)}
                    {props.moduleControl.SET_MEMBERSHIP && ClubViewManager.variant(props)}
                    {props.moduleControl.SET_MEMBERSHIP && ClubViewManager.membershipSelector(props)}
                    {props.moduleControl.SET_SHIPPING && ClubViewManager.shipping(props)}
                    {props.moduleControl.TERMS && ClubViewManager.termsAndConditions(props)}
                    {props.moduleControl.SIGNUP && ClubViewManager.renderSignup(props)}
                </React.Fragment>
            }
        </div>
    );
};